<template>
    <div
        class="border-start-light p-0 m-0 bg-light overflow-auto"
        style="border-top: none !important"
        :style="isGroupsAndPrintTabCollapsed ? 'position: absolute; right: 0;' : undefined">
        <feather
            v-if="canCollapse"
            :type="isGroupsAndPrintTabCollapsed ? 'chevrons-left' : 'chevrons-right'"
            class="pointer"
            @click="isGroupsAndPrintTabCollapsed = !isGroupsAndPrintTabCollapsed"
            v-tooltip="getTooltip($t(`labels.booking.seatingPlan.groupsAndPrint.${isGroupsAndPrintTabCollapsed ? 'show' : 'hide'}`))" />
        <div :class="{ 'd-none': isGroupsAndPrintTabCollapsed }">
            <!-- tables -->
            <div class="accordion" id="tablesAccordion" style="flex-direction: unset !important" v-if="showTables">
                <div class="card border-0">
                    <div class="card-header p-0" id="tablesHeader">
                        <h2 class="mb-0">
                            <button
                                class="btn w-100 h-100 text-start"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#tablesList"
                                aria-expanded="true"
                                aria-controls="tablesList"
                                @click="isTableListExpanded = !isTableListExpanded">
                                {{ $tl("labels.booking.seatingPlan.tables") }}
                                <feather
                                    v-tooltip="getTooltipNotice('notices.booking.tablePlan.tables')"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                                <feather v-if="isTableListExpanded" type="chevron-down" />
                                <feather v-else type="chevron-right" />
                            </button>
                        </h2>
                    </div>
                    <div>
                        <div
                            :ref="`tableList`"
                            id="tablesList"
                            class="collapse show border-0"
                            aria-labelledby="tablesHeader"
                            data-bs-parent="#tablesAccordion">
                            <div v-if="currentRoom && has_right_to_update_seatingplan" class="list-group border-0">
                                <template v-for="(table, index) in sortedTables">
                                    <li
                                        class="list-group-item"
                                        v-if="table.seats > 0"
                                        :key="table.id"
                                        :draggable="sortable"
                                        :class="{ selected: selectedTablesId.includes(table.id) }"
                                        style="cursor: pointer"
                                        @dragstart="startListDrag('tables', table, $event)"
                                        @dragover.prevent
                                        @drop.stop="dropListItem('tables', table, $event)"
                                        @dragend="endListDrag"
                                        @click="tableSelected($event, table)">
                                        <div class="h-100 w-100">
                                            <div class="float-start">
                                                <svg
                                                    v-if="sortable"
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    fill="none"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    class="css-i6dzq1 feather me-1"
                                                    style="color: #aaaaaa">
                                                    <line x1="3" y1="9" x2="21" y2="9"></line>
                                                    <line x1="3" y1="15" x2="21" y2="15"></line>
                                                </svg>
                                                {{ table.seats ? $tl("labels.booking.seatingPlan.table.title") : "" }}
                                                {{ table.name }}
                                            </div>
                                            <div class="float-end" v-if="table.seats">
                                                {{ capitalize($tl("labels.pax")) }} :
                                                {{ table.enabledPax.length > 0 ? table.enabledPax.join(" ,") : table.seats }}
                                            </div>
                                        </div>
                                    </li>
                                </template>
                            </div>
                            <div class="list-group border-0" v-else-if="currentRoom && !has_right_to_update_seatingplan">
                                <template v-for="(table, index) in sortedTables">
                                    <li
                                        class="list-group-item"
                                        v-if="table.seats > 0"
                                        :key="table.id"
                                        :class="{ selected: selectedTablesId.includes(table.id) }"
                                        style="cursor: pointer"
                                        @click="tableSelected($event, table)">
                                        <div class="h-100 w-100">
                                            <div class="float-start">
                                                <svg
                                                    v-if="sortable && has_right_to_update_seatingplan"
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    fill="none"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    class="css-i6dzq1 feather me-1"
                                                    style="color: #aaaaaa">
                                                    <line x1="3" y1="9" x2="21" y2="9"></line>
                                                    <line x1="3" y1="15" x2="21" y2="15"></line>
                                                </svg>
                                                {{ $tl("labels.booking.seatingPlan.table.title") }} {{ table.name }}
                                            </div>
                                            <div class="float-end">
                                                {{ capitalize($tl("labels.pax")) }} :
                                                {{ table.enabledPax.length > 0 ? table.enabledPax.join(" ,") : table.seats }}
                                            </div>
                                        </div>
                                    </li>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- groups -->
            <div class="accordion" id="groupsAccordion" v-if="showGroups">
                <div class="card border-0">
                    <div class="card-header p-1" id="groupsHeader">
                        <h2 class="mb-0">
                            <button
                                class="btn w-100 h-100 text-start"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#groupsList"
                                aria-expanded="true"
                                aria-controls="groupsList"
                                @click="isGroupsListExpanded = !isGroupsListExpanded">
                                {{ $tl("labels.booking.seatingPlan.groups") }}
                                <feather
                                    v-if="sortable"
                                    v-tooltip="getTooltipNotice('notices.booking.tablePlan.groups')"
                                    type="info"
                                    class="ms-1 pointer tooltip-infos" />
                                <feather v-if="isGroupsListExpanded" type="chevron-down" />
                                <feather v-else type="chevron-right" />
                            </button>
                        </h2>
                    </div>
                    <div
                        :ref="`groupsList`"
                        id="groupsList"
                        class="collapse show border-0"
                        aria-labelledby="groupsHeader"
                        data-bs-parent="#groupsAccordion">
                        <ul class="list-group border-0" v-if="currentRoom">
                            <template v-for="(group, index) in sortedGroups">
                                <li
                                    class="list-group-item p-0"
                                    :key="group.id ?? index"
                                    :draggable="sortable && has_right_to_update_seatingplan"
                                    :class="{ selected: selectedGroupId === group.id }"
                                    style="cursor: pointer"
                                    @dragstart="startListDrag('groups', group, $event)"
                                    @dragover.prevent
                                    @drop.stop="dropListItem('groups', group, $event)"
                                    @dragend="endListDrag">
                                    <div
                                        class="accordion"
                                        :id="`group-${group.id}-accordion`"
                                        :style="getGroupStyle(group)"
                                        :class="getGroupClasses(group)"
                                        @mouseenter.stop="onGroupMouseEnter($event, group)"
                                        @mouseleave.stop="onGroupMouseLeave($event, group)"
                                        @dragenter.stop="onGroupMouseEnter($event, group)"
                                        @dragleave.stop="onGroupMouseLeave($event, group)"
                                        @dragover.prevent
                                        @drop="onDropOnGroup($event, group)">
                                        <div class="card border-0">
                                            <div class="card-header p-1" :id="`group-${group.id}-header`">
                                                <div class="d-inline">
                                                    <span @click="groupSelected($event, group)">
                                                        <svg
                                                            v-if="sortable && has_right_to_update_seatingplan"
                                                            viewBox="0 0 24 24"
                                                            width="24"
                                                            height="24"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            fill="none"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            class="css-i6dzq1 feather me-1"
                                                            style="color: #aaaaaa">
                                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                                            <line x1="3" y1="15" x2="21" y2="15"></line>
                                                        </svg>
                                                        {{ $tl("labels.booking.seatingPlan.group.titleShort") }}
                                                        {{ group.name }}
                                                    </span>
                                                    <button
                                                        class="btn"
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        :data-bs-target="`#group-${group.id}-list`"
                                                        aria-expanded="true"
                                                        :aria-controls="`group-${group.id}-list`"
                                                        @click="group.expanded = !group.expanded">
                                                        <feather v-if="group.expanded" type="chevron-down" />
                                                        <feather v-else type="chevron-right" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div
                                                :id="`group-${group.id}-list`"
                                                class="collapse border-0"
                                                :aria-labelledby="`group-${group.id}-header`"
                                                :data-bs-parent="`#group-${group.id}-accordion`">
                                                <ul class="list-group border-0" v-if="currentRoom">
                                                    <li
                                                        class="list-group-item"
                                                        v-for="(table, index) in group.tables"
                                                        style="cursor: pointer"
                                                        :style="getTableStyle(table)"
                                                        :key="table.id ?? index">
                                                        <div class="h-100 w-100">
                                                            <div class="float-start">
                                                                {{ $tl("labels.booking.seatingPlan.table.title") }}
                                                                {{ table.name }}
                                                            </div>
                                                            <div class="float-end">
                                                                {{ $tc("labels.booking.seatingPlan.countSeats", table.seats) }}
                                                            </div>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
            </div>
            <div v-if="showRooms && rooms.length > 0" class="accordion" id="roomsAccordion" style="flex-direction: unset !important">
                <div class="card border-0">
                    <div class="card-header p-0" id="roomsHeader">
                        <h2 class="mb-0">
                            <button
                                class="btn w-100 h-100 text-start"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#roomsList"
                                aria-expanded="true"
                                aria-controls="roomsList"
                                @click="isRoomsListExpanded = !isRoomsListExpanded">
                                {{ $tl("labels.booking.seatingPlan.printing") }}
                                <feather v-if="isRoomsListExpanded" type="chevron-down" />
                                <feather v-else type="chevron-right" />
                            </button>
                        </h2>
                    </div>
                    <div>
                        <div
                            :ref="`roomsList`"
                            id="roomsList"
                            class="collapse border-0"
                            aria-labelledby="roomsHeader"
                            data-bs-parent="#roomsAccordion">
                            <div class="list-group border-0">
                                <template v-for="(room, index) in rooms">
                                    <li class="list-group-item" :key="index">
                                        <div class="d-flex justify-content-between">
                                            <span class="pointer" @click="rooms[index].print = !rooms[index].print">
                                                {{ rooms[index].name }}
                                            </span>
                                            <label class="form-label container-box m-0">
                                                <input type="checkbox" v-model="rooms[index].print" />
                                                <span class="checkmark"></span>
                                            </label>
                                        </div>
                                    </li>
                                </template>
                                <div class="mx-auto mt-2">
                                    <button
                                        class="btn btn-sm btn-circle btn-success"
                                        :disabled="!atLeastOneRoomSelectedForPrint || isPrinting"
                                        @click.prevent="printSeatingPlan()">
                                        {{ $tl("labels.form.actions.print") }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        currentRoom: {
            required: true,
        },
        selectedTablesId: {
            default: () => [],
        },
        selectedTables: {
            default: () => [],
        },
        selectedGroupId: {
            default: null,
        },
        showTables: {
            default: true,
        },
        showGroups: {
            default: true,
        },
        showRooms: {
            default: true,
        },
        disablePointerEvents: {
            default: false,
        },
        sortable: {
            default: false,
        },
        rooms: {
            default: () => [],
        },
        canCollapse: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isMounted: false,
            isTableListExpanded: true,
            isGroupsListExpanded: true,
            isRoomsListExpanded: false,
            isPrinting: false,
            listDrag: {
                type: null,
                id: null,
            },
        };
    },
    mounted() {
        this.isMounted = true;
    },
    computed: {
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_update_seatingplan() {
            return this.rights.includes("booking.seatingplan.update");
        },
        isGroupsAndPrintTabCollapsed: {
            get() {
                return this.canCollapse && this.$store.getters["userPreferences/isGroupsAndPrintTabCollapsed"];
            },
            set(newVal) {
                this.$store.dispatch("userPreferences/setIsGroupsAndPrintTabCollapsed", newVal);

                this.$emit("resized");
            },
        },
        sortedTables: {
            get() {
                return this.currentRoom.tables
                    .map((table, index) => ({
                        ...table,
                        baseIndex: index,
                    }))
                    .sort((leftTable, rightTable) => {
                        if (leftTable.order === rightTable.order) {
                            return leftTable.baseIndex - rightTable.baseIndex;
                        }

                        return leftTable.order < rightTable.order ? -1 : 1;
                    });
            },
            set(value) {
                const result = [];
                value.forEach((table, index) => {
                    const foundIndex = this.currentRoom.tables.findIndex((currentTable) => currentTable.id === table.id);

                    if (foundIndex > -1) {
                        result.push({
                            index: foundIndex,
                            value: {
                                ...this.currentRoom.tables[foundIndex],
                                order: Number.parseInt(index, 10),
                            },
                        });
                    }
                });
                this.$emit("tables-changed", result);
            },
        },
        sortedGroups: {
            get() {
                return this.currentRoom.groups
                    .map((group, index) => ({
                        ...group,
                        baseIndex: index,
                    }))
                    .sort((leftGroup, rightGroup) => {
                        if (leftGroup.order === rightGroup.order) {
                            return leftGroup.baseIndex - rightGroup.baseIndex;
                        }

                        return leftGroup.order < rightGroup.order ? -1 : 1;
                    });
            },
            set(value) {
                const result = [];
                value.forEach((group, index) => {
                    const foundIndex = this.currentRoom.groups.findIndex((currentGroup) => currentGroup.id === group.id);

                    if (foundIndex > -1) {
                        result.push({
                            index: foundIndex,
                            value: {
                                ...this.currentRoom.groups[foundIndex],
                                order: Number.parseInt(index, 10),
                            },
                        });
                    }
                });

                this.$emit("groups-changed", result);
            },
        },
        getTablesTreeViewData() {
            let result = [];

            if (this.currentRoom) {
                this.currentRoom.tables.forEach((table) => {
                    result.push({
                        name: table.name,
                    });
                });
            }

            return result;
        },
        selectedGroup() {
            return this.selectedGroupId ? this.findGroupById(this.selectedGroupId) : null;
        },
        atLeastOneRoomSelectedForPrint() {
            return this.rooms.length > 0 && this.rooms.some((r) => r.print);
        },
    },
    methods: {
        startListDrag(type, item, event) {
            if (!this.sortable || !this.has_right_to_update_seatingplan) {
                event.preventDefault();
                return;
            }

            this.listDrag = { type, id: item.id };
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", `seating-plan-list:${type}:${item.id}`);
            this.$emit("list-drag-start");
        },
        dropListItem(type, target, event) {
            event.preventDefault();

            if (this.listDrag.type !== type || this.listDrag.id === target.id) {
                return;
            }

            const items = type === "tables" ? [...this.sortedTables] : [...this.sortedGroups];
            const sourceIndex = items.findIndex((item) => item.id === this.listDrag.id);
            const targetIndex = items.findIndex((item) => item.id === target.id);

            if (sourceIndex < 0 || targetIndex < 0) {
                return;
            }

            const [movedItem] = items.splice(sourceIndex, 1);
            items.splice(targetIndex, 0, movedItem);

            if (type === "tables") {
                this.sortedTables = items;
            } else {
                this.sortedGroups = items;
            }

            this.endListDrag();
        },
        endListDrag() {
            this.listDrag = { type: null, id: null };
        },
        tableSelected(event, table) {
            if (!event.ctrlKey && !event.metaKey) {
                this.$emit("unselect-all-tables");
            }

            this.$nextTick(() => {
                if (this.selectedGroup) {
                    this.selectedGroup.tables.forEach((t) => {
                        this.selectedTablesId.push(t.id);
                    });
                    this.$emit("group-selected", null);
                }

                this.$emit("table-selected", table.group);
            });
        },
        groupSelected(event, group) {
            this.$emit("group-selected", group);
        },
        findGroupById(id) {
            for (var group of this.currentRoom.groups) {
                if (group.id === id) {
                    return group;
                }
            }

            return null;
        },
        onGroupMouseEnter(event, group) {
            this.$emit("highlight-group", { group, event });
        },
        onGroupMouseLeave(event, group) {
            this.$emit("unhighlight", { event });
        },
        onDropOnGroup(event, group) {
            if (this.sortable) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            this.$emit("droped-on-group", { event, group });
        },
        getGroupClasses(group) {
            return {
                "no-child-events": this.disablePointerEvents,
                "group-disabled": group.used || !group.isEnabledForService,
            };
        },
        getGroupStyle(group) {
            var result = "";

            if (group.slots) {
                const groupReservations = group.slots
                    .map((s) => s.reservations_pivot)
                    .flat()
                    .filter((rp, index, self) => {
                        return rp.reservation && self.findIndex((rp2) => rp2.reservation && rp.reservation.id === rp2.reservation.id) === index;
                    })
                    .map((rp) => rp.reservation);

                if (!this.$_.isEmpty(groupReservations)) {
                    if (groupReservations.length > 1) {
                        var percentageUsed = 0;
                        var percentagePerReservation = Math.floor(100 / groupReservations.length);
                        result = "background: linear-gradient(90deg,";

                        for (var reservationIndex in groupReservations) {
                            const reservation = groupReservations[reservationIndex];

                            if (reservationIndex == groupReservations.length - 1) {
                                result += ` ${reservation.color} ${100 - percentageUsed}%);`;
                            } else {
                                result += ` ${reservation.color} ${percentagePerReservation}%,`;
                                percentageUsed += percentagePerReservation;
                            }
                        }
                    } else if (groupReservations.length === 1) {
                        result = `background-color: ${groupReservations[0].color}`;
                    }
                }
            }

            return result;
        },
        getTableStyle(table) {
            var result = "";

            if (table.slots) {
                const tableReservations = table.slots
                    .map((s) => s.reservations_pivot)
                    .flat()
                    .filter((rp, index, self) => {
                        return rp.reservation && self.findIndex((rp2) => rp2.reservation && rp2.reservation.id === rp.reservation.id) === index;
                    })
                    .map((rp) => rp.reservation);

                if (!this.$_.isEmpty(tableReservations)) {
                    if (tableReservations.length > 1) {
                        var percentageUsed = 0;
                        var percentagePerReservation = Math.floor(100 / tableReservations.length);
                        result = "background: linear-gradient(90deg,";

                        for (var reservationIndex in tableReservations) {
                            const reservation = tableReservations[reservationIndex];

                            if (reservationIndex == tableReservations.length - 1) {
                                result += ` ${reservation.color} ${100 - percentageUsed}%);`;
                            } else {
                                result += ` ${reservation.color} ${percentagePerReservation}%,`;
                                percentageUsed += percentagePerReservation;
                            }
                        }
                    } else if (tableReservations.length === 1) {
                        result = `background-color: ${tableReservations[0].color}`;
                    }
                }
            }
            return result;
        },
        getNextReservationFromNow(reservationPivotArray) {
            let reservationFound = null;
            let reservationFoundNowDiff = null;

            const nowDateTime = this.getDateTime();
            const reservationArray = reservationPivotArray.map((rp) => rp.reservation).filter((rp) => rp);

            for (const reservation of reservationArray) {
                if (!reservationFound) {
                    reservationFound = reservation;
                    reservationFoundNowDiff = this.getDateTime(reservation.reservation_datetime).diff(nowDateTime, "minutes").minutes;
                } else {
                    var tmpNowDiff = this.getDateTime(reservation.reservation_datetime).diff(nowDateTime, "minutes").minutes;
                    let swipe = false;

                    if (reservationFoundNowDiff < 0 && tmpNowDiff > 0) {
                        swipe = true;
                    } else if (reservationFoundNowDiff < 0 && tmpNowDiff < 0 && reservationFoundNowDiff < tmpNowDiff) {
                        swipe = true;
                    } else if (reservationFoundNowDiff > 0 && tmpNowDiff > 0 && reservationFoundNowDiff > tmpNowDiff) {
                        swipe = true;
                    }

                    if (swipe) {
                        reservationFound = reservation;
                        reservationFoundNowDiff = tmpNowDiff;
                    }
                }
            }

            return reservationFound;
        },
        printSeatingPlan() {
            const roomsIds = this.rooms.filter((r) => r.print).map((r) => r.id);

            this.$emit("print", { roomsIds });
        },
    },
};
</script>
